import React, { useReducer, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
  Link,
} from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { get } from "lodash";
import toast from "utils/ToastNotistack";
import MainCard from "components/MainCard";
import T from "utils/constants/T";
import { StyledBodyTypography } from "../CompanyTab/CompanyInfo";
import CashDCard from "components/common/CashDCard";
import CashDAutocomplete from "components/common/CashDAutocomplete";
import { BANKING_ENVIRONMENT_LIST } from "utils/constants/companyDetails";
import ViewBankingDetails from "./ViewBankingDetails";
import {
  getExistingPaymentPartner,
  getPaymentPartnersById,
  updateBankingEnvironment,
  updatePaymentPartner,
} from "store/reducers/companyBankingSettings";
import CashDDialog from "components/common/CashDDialog";
import ChangeLenderDetails from "./ChangeLenderDetails";
import { updateCompanyDetails } from "store/reducers/companyTabDetails";
import "../../companyDetails.css";
import "./banking.css";
import LenderCreationForm from "../LenderSettingsTab/LenderCreationForm";
import {
  fetchLenderAccount,
  getLenderAccountList,
} from "store/reducers/lenderSettings";
import CashDCricularProgress from "components/common/CashDCricularProgress";
import { paymentPartnerAlertNotes } from "utils/constants/common";
import AlertConfirmationModal from "components/common/AlertConfirmationModal";

type LocalStateType = {
  paymentPartner: any;
  environment: string;
  previousPaymentPartner: any;
  openLenderChangeModal: boolean;
  openLenderAccountModal: boolean;
  paymentPartnerAlertModal: any;
};

const NO_PAYMENT_PARTNER = { code: "" };

const EditPayrollSettings: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedCompany = useSelector(
    (state: any) => state.dashboardSearch.selectedCompany
  );
  const selectedCompanyId = get(selectedCompany, "_id", "");
  const { companyName, paymentSystemId, bankingEnvironment } = useSelector(
    (state: any) => state.companyTabData
  );
  const { isLoading } = useSelector((state: any) => state.lenderSettings);
  const {
    paymentPartnerList,
    existingPaymentPartner,
    accountNumber,
    lenderId,
    fundingType,
    nameOfBank,
    balance,
    accountName,
    bsb,
    interestRate,
  } = useSelector((state: any) => state.bankingSettingsTabData);

  const { lenderAccount } = useSelector(
    (state: any) => state.lenderSettings,
    shallowEqual
  );

  const existingPaymentPartnerObj = useMemo(
    () =>
      paymentPartnerList.find(
        (partner: any) => partner.code === existingPaymentPartner
      ) || NO_PAYMENT_PARTNER,
    [paymentPartnerList, existingPaymentPartner]
  );

  // Local state management
  const [localState, setLocalState] = useReducer(
    (prevState: LocalStateType, newState: Partial<LocalStateType>) => ({
      ...prevState,
      ...newState,
    }),
    {
      previousPaymentPartner: "",
      paymentPartner: existingPaymentPartnerObj,
      environment: bankingEnvironment ? T.LIVE : T.TEST,
      openLenderChangeModal: false,
      openLenderAccountModal: false,
      paymentPartnerAlertModal: { open: false, status: T.CONFIRM },
    }
  );

  const {
    environment,
    paymentPartner,
    openLenderChangeModal,
    openLenderAccountModal,
    previousPaymentPartner,
    paymentPartnerAlertModal,
  } = localState;

  useEffect(() => {
    if (paymentSystemId) {
      dispatch(getPaymentPartnersById(paymentSystemId));
    } else {
      dispatch(getExistingPaymentPartner({ existingPaymentPartner: {} }));
      setLocalState({ paymentPartner: {} });
    }
  }, [paymentSystemId, dispatch]);

  useEffect(() => {
    if (selectedCompanyId && existingPaymentPartner?.code === T.MONOOVA) {
      dispatch(
        getLenderAccountList(selectedCompanyId, existingPaymentPartner.code)
      );
    }
  }, [selectedCompanyId, existingPaymentPartner, dispatch]);

  // Save settings handler
  const handleSaveSettings = useCallback(async () => {
    try {
      if (paymentPartnerAlertModal.status === T.CONFIRM) {
        // Save Payment Partner
        if (paymentPartner && paymentPartner._id) {
          const paymentPartnerPayload = {
            companyId: selectedCompanyId,
            paymentSystemId: paymentPartner._id,
          };
          dispatch(updatePaymentPartner(paymentPartnerPayload));
        }
      }

      if (lenderId) {
        // Save Banking Environment
        const bankingPayload = {
          lenderId,
          is_monoova_live_mode: environment === T.LIVE,
        };
        dispatch(updateBankingEnvironment(selectedCompanyId, bankingPayload));
        // Update company details locally
        dispatch(
          updateCompanyDetails({
            key: "bankingEnvironment",
            value: bankingPayload.is_monoova_live_mode,
          })
        );
      }

      // Navigate back after successful saving
      navigate("/companyDetails/bankingSettings/view");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast("Error saving settings", { variant: "error" });
    }
  }, [
    lenderId,
    environment,
    paymentPartner,
    selectedCompanyId,
    paymentPartnerAlertModal,
    dispatch,
    navigate,
  ]);

  const handleBankingEnvironmentChange = useCallback(
    (type: string, newValue: any) => {
      setLocalState({ [type]: newValue } as Partial<LocalStateType>);
    },
    []
  );

  const handlePaymentPartnerChange = useCallback(
    (type: keyof LocalStateType, newValue: any) => {
      setLocalState({
        [type]: newValue,
        ...(lenderId && {
          previousPaymentPartner: paymentPartner,
          paymentPartnerAlertModal: { open: true, status: "" },
        }),
      });
    },
    [paymentPartner, lenderId]
  );

  const closePaymentPartnerAlertModal = useCallback(() => {
    setLocalState({
      paymentPartnerAlertModal: { open: false, status: "" },
      paymentPartner: previousPaymentPartner,
    });
  }, [paymentPartnerAlertModal, previousPaymentPartner]);

  const handleLenderChangeModal = useCallback(
    () =>
      setLocalState({
        openLenderChangeModal: !localState.openLenderChangeModal,
      }),
    [localState]
  );

  useEffect(() => {
    if (selectedCompanyId && paymentPartner?.code) {
      dispatch(fetchLenderAccount(selectedCompanyId, paymentPartner.code));
    }
  }, [selectedCompanyId, paymentPartner?.code]);

  const handleLenderAccountModal = useCallback(
    (value: boolean) => {
      setLocalState({
        openLenderAccountModal: value,
      });
    },
    [openLenderAccountModal, selectedCompanyId, paymentPartner]
  );

  const renderViewBankingData = useMemo(
    () => ({
      accountName,
      fundingType,
      nameOfBank,
      bsb,
      accountNumber,
      balance,
      interestRate,
    }),
    [
      accountName,
      fundingType,
      nameOfBank,
      bsb,
      accountNumber,
      balance,
      interestRate,
    ]
  );

  const paymentPartnerValue = useMemo(() => {
    if (typeof paymentPartner === "object" && paymentPartner?.code) {
      return paymentPartner;
    }
    if (typeof paymentPartner === "string" && paymentPartner) {
      return paymentPartner;
    }
    return null;
  }, [paymentPartner]);

  return (
    <>
      <MainCard title={companyName}>
        <CashDCard borderRadius="20px">
          <Grid container rowSpacing={4} columnSpacing={15}>
            <Grid item xs={12}>
              <Grid item xs={12}>
                <Grid container className="top-box" rowSpacing={1}>
                  <Grid item xs={3}>
                    <StyledBodyTypography>
                      {T.PAYMENT_PARTNER_SELECTION}
                    </StyledBodyTypography>
                  </Grid>
                  <Grid item xs={4}>
                    <CashDAutocomplete
                      placeholder={T.CHOOSE_PAYMENT_PARTNER}
                      fullWidth
                      listDetails={paymentPartnerList}
                      value={paymentPartnerValue}
                      getByLabelText={(option: any) => option.code}
                      onHandleChange={(event, newValue) =>
                        handlePaymentPartnerChange("paymentPartner", newValue)
                      }
                    />
                  </Grid>
                  <Grid item xs={2} sx={{ marginLeft: "10px" }}>
                    <Button
                      variant="contained"
                      className="main-card-button"
                      onClick={handleSaveSettings}
                      disabled={
                        existingPaymentPartnerObj?.code ===
                          paymentPartner?.code || !paymentPartner?.code
                      }
                    >
                      {T.UPDATE}
                    </Button>
                  </Grid>
                  <Grid item xs={3}>
                    <StyledBodyTypography>{T.ENVIRONMENT}</StyledBodyTypography>
                  </Grid>
                  <Grid item xs={4}>
                    <CashDAutocomplete
                      disabled={!lenderId}
                      placeholder={T.CHOOSE_SETTING}
                      fullWidth
                      listDetails={BANKING_ENVIRONMENT_LIST}
                      value={environment}
                      getByLabelText={(option: any) => option}
                      onHandleChange={(event, newValue) =>
                        handleBankingEnvironmentChange("environment", newValue)
                      }
                    />
                  </Grid>
                  <Grid item xs={2} sx={{ marginLeft: "10px" }}>
                    <Button
                      variant="contained"
                      className="main-card-button"
                      onClick={handleSaveSettings}
                      disabled={bankingEnvironment === (environment === "Live")}
                    >
                      {T.UPDATE}
                    </Button>
                  </Grid>
                  {existingPaymentPartnerObj?.code === T.MONOOVA &&
                    paymentPartner?.code === T.MONOOVA && (
                      <Grid container mt={2}>
                        <Grid item xs={3}>
                          <StyledBodyTypography>
                            {T.LENDER_ACCOUNT}
                          </StyledBodyTypography>
                        </Grid>
                        <Grid item xs={3} display="flex" justifyContent="left">
                          {isLoading ? (
                            <CashDCricularProgress size={18} />
                          ) : (
                            <Link
                              component="button"
                              variant="h6"
                              onClick={() => handleLenderAccountModal(true)}
                            >
                              {lenderAccount
                                ? `${lenderAccount.lender_name} Account`
                                : "Create Lender Account"}
                            </Link>
                          )}
                        </Grid>
                      </Grid>
                    )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CashDCard>
        {lenderId ? (
          <CashDCard borderRadius="20px">
            <ViewBankingDetails {...renderViewBankingData} />
            <Divider />
            <Box className="add-button">
              <Button variant="contained" onClick={handleLenderChangeModal}>
                {T.CHANGE}
              </Button>
            </Box>
          </CashDCard>
        ) : (
          <CashDCard borderRadius="20px">
            <Typography className="lender-heading">
              {T.LENDER_DETAILS}
            </Typography>
            <Box className="add-button">
              <Button variant="contained" onClick={handleLenderChangeModal}>
                {T.ADD}
              </Button>
            </Box>
          </CashDCard>
        )}
      </MainCard>

      <CashDDialog
        open={openLenderChangeModal}
        handleClose={handleLenderChangeModal}
      >
        <Box className="header-box">
          <Typography variant="h4" className="view-table-heading">
            {T.SELECT_LENDER}
          </Typography>
          <IconButton onClick={handleLenderChangeModal}>
            <CancelOutlinedIcon />
          </IconButton>
        </Box>
        <ChangeLenderDetails
          selectedCompanyId={selectedCompanyId}
          handleLenderChangeModal={handleLenderChangeModal}
        />
      </CashDDialog>
      {openLenderAccountModal && !isLoading && (
        <CashDDialog
          open={true}
          title={
            lenderAccount
              ? `${lenderAccount.lender_name} Account`
              : T.LENDER_ACCOUNT
          }
          handleClose={() => handleLenderAccountModal(false)}
          fullWidth
        >
          <LenderCreationForm
            providerType={existingPaymentPartnerObj.code}
            companyId={selectedCompanyId}
          />
        </CashDDialog>
      )}
      <AlertConfirmationModal
        open={paymentPartnerAlertModal.open}
        onClose={closePaymentPartnerAlertModal}
        onConfirm={() => {
          setLocalState({
            paymentPartnerAlertModal: { open: false, status: T.CONFIRM },
          });
        }}
        title="Change Payment Partner"
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Notes:
        </Typography>
        <ul style={{ marginBottom: 3 }}>
          {paymentPartnerAlertNotes.map((note) => (
            <li key={note} style={{ marginBottom: 1 }}>
              {note}
            </li>
          ))}
        </ul>
      </AlertConfirmationModal>
    </>
  );
};

export default React.memo(EditPayrollSettings);
